import { ALL_FLAG_KEYS, ASKED_FLAGS } from '@/data/applicationFlags'
import { environmentTypeOf } from '@/data/environmentTypes'
import { pipelineValueOf } from '@/data/productLines'
import type { InfraWizardData } from '@/stores/infrastructureWizard'
import { effectiveServerMode } from '@/stores/infrastructureWizard'
import type { AppFlagKey, ApplicationAnswers, ApplicationConfig, ApplicationCreationConfig } from '@/types/application'
import { dbNameStyle, resolveServerName } from '@/utils/infraValidation'

/** What the details are worked out from: the earlier answers, as they stand on the Application (ERP) step. */
export interface ApplicationContext {
  infra: InfraWizardData
  customerName: string
  productLineId: string
  tlsPort: string
  sysDb: string
  mainDb: string
}

export const emptyApplicationAnswers = (): ApplicationAnswers => ({
  createFor: null,
  profileId: '',
  traverseDatabase: { useMasterLogin: false, login: '', password: '' },
  membershipDatabase: { useMasterLogin: false, useTraverseCredentials: false, login: '', password: '' },
  flags: {},
})

// ─── Taken from the first answers ────────────────────────────────────────────

/** The RDS server's host name: the existing server chosen, or the name generated for the new one. */
export function rdsServerName(infra: InfraWizardData): string {
  if (effectiveServerMode(infra.product, infra.rdsServer.mode, 'rds') === 'existing') return infra.rdsServer.existing?.name ?? ''
  const resolved = resolveServerName(infra, 'rds', 'host')
  return resolved.ok ? resolved.name : ''
}

/**
 * The SQL server the License Server Manager connects to: the existing server chosen, or the new one's name. An Azure SQL server is
 * reached at <name>.database.windows.net; a named SQL instance, where one was asked for, follows a backslash.
 */
export function dbServerAddress(infra: InfraWizardData): string {
  const mode = effectiveServerMode(infra.product, infra.dbServer.mode, 'db')
  const azure = mode === 'dedicated' && dbNameStyle(infra.level) === 'azure'
  let name = ''
  if (mode === 'existing') name = infra.dbServer.existing?.name ?? ''
  else if (mode === 'dedicated') {
    const resolved = resolveServerName(infra, 'db', dbNameStyle(infra.level))
    name = resolved.ok ? resolved.name : ''
  }
  if (name === '') return ''
  if (azure) return `${name}.database.windows.net`
  const instance = infra.sqlInstance.required === true ? infra.sqlInstance.instanceName.trim() : ''
  return instance === '' ? name : `${name}\\${instance}`
}

/** True when "Production or Sandbox?" is asked: in a production environment, which holds both. */
export const asksCreateFor = (infra: Pick<InfraWizardData, 'environment'>): boolean => environmentTypeOf(infra.environment) === 'production'

/**
 * The Product ID: the license key entered above it. In a production environment it is the Production key or the Sandbox key, whichever
 * the application is being created for (nothing until that is answered); in any other it is the License key.
 */
export function licenseProductId(infra: InfraWizardData, answers: Pick<ApplicationAnswers, 'createFor'>): string {
  if (!asksCreateFor(infra)) return infra.license.licenseKey.trim()
  if (answers.createFor === 'Production') return infra.license.productionKey.trim()
  if (answers.createFor === 'Sandbox') return infra.license.sandboxKey.trim()
  return ''
}

/** The flags that are not asked: the answer is the one given the first time. */
export function derivedFlagValues(infra: InfraWizardData, answers: ApplicationAnswers): Partial<Record<AppFlagKey, boolean>> {
  const on = (addOn: { enabled: boolean }) => addOn.enabled
  const iis = [infra.customerPortal, infra.wms, infra.travApiPortal, infra.webApiPortal, infra.adminPortal]
  return {
    MasterLogin: answers.traverseDatabase.useMasterLogin || answers.membershipDatabase.useMasterLogin,
    IISApps: iis.some(on),
    InstallWMS: on(infra.wms),
    InstallAPI: on(infra.travApiPortal) || on(infra.webApiPortal),
    InstallAP: on(infra.adminPortal),
    InstallCP: on(infra.customerPortal),
  }
}

/** Every flag, asked or taken from an earlier answer. A flag nobody answered is N. */
export function flagValues(infra: InfraWizardData, answers: ApplicationAnswers): Record<AppFlagKey, boolean> {
  const derived = derivedFlagValues(infra, answers)
  return Object.fromEntries(ALL_FLAG_KEYS.map((key) => [key, derived[key] ?? answers.flags[key] ?? false])) as Record<AppFlagKey, boolean>
}

/** The first seven inputs of New-TraverseEnvironment.ps1, all taken from earlier answers. */
export function scriptInputs(context: ApplicationContext, answers: Pick<ApplicationAnswers, 'createFor'>): Omit<ApplicationCreationConfig, 'flags'> {
  return {
    CustomerName: context.customerName.trim(),
    ProductLine: pipelineValueOf(context.productLineId),
    RDSServer: rdsServerName(context.infra),
    TLSPort: context.tlsPort.trim(),
    SYSDB: context.sysDb,
    CompID: context.mainDb,
    // A production environment can hold Production or Sandbox: the answer. Any other is a Sandbox.
    EnvType: asksCreateFor(context.infra) ? (answers.createFor ?? '') : 'Sandbox',
  }
}

// ─── What is asked ─────────────────────────────────────────────────────────

/** What is still to answer. Each is a message for the "To continue:" list. */
export function applicationAnswerProblems(answers: ApplicationAnswers, options: { asksCreateFor?: boolean } = {}): string[] {
  const problems: string[] = []
  const ask = (what: string) => problems.push(`Enter the ${what} in the Application (ERP) step.`)
  if (options.asksCreateFor && answers.createFor === null) problems.push('Choose Production or Sandbox in the Application (ERP) step.')
  if (answers.profileId.trim() === '') ask('Profile ID')
  const traverse = answers.traverseDatabase
  if (!traverse.useMasterLogin) {
    if (traverse.login.trim() === '') ask('TRAVERSE database login')
    if (traverse.password === '') ask('TRAVERSE database password')
  }
  const membership = answers.membershipDatabase
  if (!membership.useMasterLogin && !membership.useTraverseCredentials) {
    if (membership.login.trim() === '') ask('membership database login')
    if (membership.password === '') ask('membership database password')
  }
  return problems
}

/** What is saved with the deployment: the answers, and everything taken from earlier answers, under the names the script and the License Server Manager use. */
export function buildApplicationConfig(context: ApplicationContext, answers: ApplicationAnswers): ApplicationConfig {
  const { infra } = context
  const sqlServer = dbServerAddress(infra)
  const traverse = answers.traverseDatabase
  const membership = answers.membershipDatabase
  const traverseLogin = traverse.useMasterLogin ? { login: '', password: '' } : { login: traverse.login.trim(), password: traverse.password }
  const membershipLogin =
    membership.useMasterLogin || membership.useTraverseCredentials ? { login: '', password: '' } : { login: membership.login.trim(), password: membership.password }
  const flags = flagValues(infra, answers)
  return {
    licenseServer: {
      profileId: answers.profileId.trim(),
      traverseDatabase: { sqlServer, useMasterLogin: traverse.useMasterLogin, ...traverseLogin },
      membershipDatabase: { sqlServer, useMasterLogin: membership.useMasterLogin, useTraverseCredentials: membership.useTraverseCredentials, ...membershipLogin },
      defaultDb: context.mainDb,
      tcpPort: context.tlsPort.trim(),
      productId: licenseProductId(infra, answers),
    },
    ...(asksCreateFor(infra) && answers.createFor !== null && { createdFor: answers.createFor }),
    creation: {
      ...scriptInputs(context, answers),
      flags: Object.fromEntries(ALL_FLAG_KEYS.map((key) => [key, flags[key] ? 'Y' : 'N'])) as Record<AppFlagKey, 'Y' | 'N'>,
    },
  }
}

/** The answers again from what was saved, so the step shows them when the user comes back to it. */
export function applicationAnswersFrom(config: ApplicationConfig | undefined): ApplicationAnswers {
  if (!config) return emptyApplicationAnswers()
  const { licenseServer, creation } = config
  return {
    createFor: config.createdFor ?? null,
    profileId: licenseServer.profileId,
    traverseDatabase: { useMasterLogin: licenseServer.traverseDatabase.useMasterLogin, login: licenseServer.traverseDatabase.login, password: licenseServer.traverseDatabase.password },
    membershipDatabase: {
      useMasterLogin: licenseServer.membershipDatabase.useMasterLogin,
      useTraverseCredentials: licenseServer.membershipDatabase.useTraverseCredentials,
      login: licenseServer.membershipDatabase.login,
      password: licenseServer.membershipDatabase.password,
    },
    flags: Object.fromEntries(ASKED_FLAGS.map((flag) => [flag.key, creation.flags[flag.key] === 'Y'])),
  }
}

// ─── Review ───────────────────────────────────────────────────────────────

export interface ApplicationReviewRow {
  label: string
  value: string
  mono?: boolean
}

/** The rows the Review step shows. No password is among them. */
export function summariseApplication(config: ApplicationConfig): ApplicationReviewRow[] {
  const { licenseServer, creation } = config
  const traverse = licenseServer.traverseDatabase
  const membership = licenseServer.membershipDatabase
  const who = (master: boolean, login: string) => (master ? 'master login' : login)
  const on = ALL_FLAG_KEYS.filter((key) => creation.flags[key] === 'Y')
  return [
    { label: 'Profile ID', value: licenseServer.profileId, mono: true },
    { label: 'TRAVERSE database', value: `${traverse.sqlServer} · ${who(traverse.useMasterLogin, traverse.login)}`, mono: true },
    {
      label: 'Membership database',
      value: `${membership.sqlServer} · ${membership.useTraverseCredentials ? 'TRAVERSE credentials' : who(membership.useMasterLogin, membership.login)}`,
      mono: true,
    },
    { label: 'Default DB', value: licenseServer.defaultDb, mono: true },
    { label: 'TCP port', value: licenseServer.tcpPort, mono: true },
    { label: 'Product ID', value: licenseServer.productId, mono: true },
    { label: 'Created for', value: creation.EnvType || '—' },
    { label: 'Application set-up', value: on.length === 0 ? 'None of the optional parts' : on.join(', '), mono: true },
  ]
}
